import { CollapsibleContent } from '@radix-ui/react-collapsible';
import { Portal } from '@radix-ui/react-tooltip';

import { useState } from 'react';

import anticipateDetail from '@/utils/anticipateRender/detail';
import { markdownToComponent } from '@/utils/convertMarkdown';
import formatName from '@/utils/format/formatName';
import formatURL from '@/utils/format/formatURL';
import getCurrentIds from '@/utils/getCurrentIds';
import getIcon, { hasIcon } from '@/utils/getIcon';
import { cn } from '@/utils/lib/utils';
import { ArrowUpRight } from '@/utils/lucide';
import { BookOpen } from '@/utils/lucide';
import { Square } from '@/utils/lucide';
import { CheckCircle } from '@/utils/lucide';
import { ChevronDown } from '@/utils/lucide';
import { ChevronUp } from '@/utils/lucide';
import { ExternalLink } from '@/utils/lucide';
import { RotateCw } from '@/utils/lucide';
import openDetail from '@/utils/openDetail';
import { z_classes } from '@/utils/z';

import { EvidenceExplanation } from '@/types/Evidence';

import MessageStatusTree from '../Message/MessageStatusTree';
import { Button } from '../ui/button';
import { Collapsible, CollapsibleTrigger } from '../ui/collapsible';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';

const getSource = (e: any, item: any) =>
{
	const type = item.type ?? item.source_type ?? item.status_type;
	const id = item.id ?? item.source_id ?? item.status_id ?? item.entity_id;

	if (type === 'trial' || type === 'drug' || type === 'company')
	{
		const { session_id } = getCurrentIds();
		socket.emit('take_action', {
			action_type: `${type}_detail`,
			[`${type}_id`]: id,
			id,
			session_id
		});

		anticipateDetail();
	}
	else if (type === 'webpage')
	{
		window.open(item.url, '_blank');
	}
	else if (type === 'field')
	{
		// Not sure what to do here
	}
	else
	{
		anticipateDetail();
		openDetail(e, id);
	}
};

const ExpandableNotepad = ({ content }: { content: string }) =>
{
	const [isExpanded, setIsExpanded] = useState(false);

	return (
		<div className='pr-2 py-2'>
			<div
				className={`p-2 border bg-neutral-100 rounded-md overflow-hidden ${isExpanded ? '' : 'max-h-[160px] relative'}`}
				onClick={() =>
				{
					if (isExpanded === false) setIsExpanded(true);
				}}
			>
				<div className='relative'>
					{markdownToComponent(content, {
						spacing: false,
						size: 's'
					})}
					{isExpanded && (
						<div className='w-full flex items-center justify-center'>
							<Button
								variant={'outline'}
								className='h-9 text-sm mb-0'
								onClick={() =>
								{
									setIsExpanded(false);
								}}
							>
								{isExpanded ? 'Collapse' : 'Expand'}
								<span className='text-icon-sm'>{isExpanded ? <ChevronUp /> : <ChevronDown />}</span>
							</Button>
						</div>
					)}
				</div>
				<div
					className={`${isExpanded ? 'hidden' : 'flex items-end justify-center bg-opacity-80 bg-gradient-to-b from-transparent to-neutral-100 absolute bottom-0 left-0 right-0 h-16'}`}
				>
					<Button variant={'outline'} className='h-9 text-sm mb-2'>
						{isExpanded ? 'Collapse' : 'Expand'}
						<span className='text-icon-sm'>{isExpanded ? <ChevronUp /> : <ChevronDown />}</span>
					</Button>
				</div>
			</div>
		</div>
	);
};

const ResearchEvidencePanel = ({ evidence, onRetry }: { evidence: EvidenceExplanation; onRetry?: () => void }) =>
{
	let textEvidenceClass = 'text-neutral-500';
	let bgEvidenceClass = 'bg-neutral-500';

	if (evidence.evidence_rating === 'No Evidence' || evidence.evidence_rating === 'Minimal Evidence')
	{
		textEvidenceClass = 'text-maven-error-400';
		bgEvidenceClass = 'bg-maven-error-100';
	}
	else if (evidence.evidence_rating === 'Indirect Evidence')
	{
		textEvidenceClass = 'text-maven-indicator-400';
		bgEvidenceClass = 'bg-maven-indicator-100';
	}
	else if (evidence.evidence_rating === 'Direct Evidence')
	{
		textEvidenceClass = 'text-maven-primary-600';
		bgEvidenceClass = 'bg-maven-primary-100';
	}

	const statusTreeName = evidence?.status_tree?.data?.name ?? 'View Steps';
	let StatusTreeIcon;
	if (evidence?.status_tree?.data?.status === 'complete') StatusTreeIcon = CheckCircle;
	else StatusTreeIcon = Square;

	const sections = evidence?.status_tree?.data?.sections;

	const [open, setOpen] = useState<Record<string, boolean>>({
		[statusTreeName]: true
	});

	return (
		<div className='flex flex-col bg-white'>
			<div className='flex flex-row items-center justify-between p-2 bg-neutral-50 border-b'>
				<p className={cn('text-lg font-bold', textEvidenceClass)}>{evidence.evidence_rating}</p>
				{onRetry && (
					<Tooltip delayDuration={100}>
						<TooltipTrigger asChild>
							<Button
								variant={'ghost'}
								className='text-xs font-semibold text-neutral-400 hover:border h-8 p-2 hover:px-[7px] hover:text-neutral-700 active:text-neutral-900'
								onClick={onRetry}
							>
								<RotateCw className='text-icon-sm' />
								<span className='hidden'>Retry</span>
							</Button>
						</TooltipTrigger>
						<TooltipContent>
							<p className=''>Have AI generate a new value for this cell</p>
						</TooltipContent>
					</Tooltip>
				)}
			</div>
			<p className='p-2 text-sm'>{evidence.evidence_explanation}</p>

			{evidence.supporting_quotes && evidence.supporting_quotes.length > 0 && (
				<div className='flex flex-col p-2 gap-2'>
					<p className='text-sm font-bold'>Key Excerpts</p>
					{evidence.supporting_quotes.map((quote, index) =>
					{
						if (!quote)
						{
							return <></>;
						}

						return (
							<Tooltip delayDuration={100} key={'quote' + index}>
								<TooltipTrigger asChild>
									<button
										className='text-sm text-start gap-2 w-fit group rounded-md border p-2 hover:bg-neutral-100 active:bg-neutral-200'
										onClick={e =>
										{
											getSource(e, quote);
										}}
									>
										<p>"{quote.quote}"</p>
									</button>
								</TooltipTrigger>
								<Portal>
									<TooltipContent className={cn('md:max-w-[560px]', z_classes.overlay[2])} side='right'>
										<div className='flex flex-col gap-2 p-2 rounded-md'>
											<p className='text-neutral-900 font-semibold'>{quote.title}</p>
											{quote.metadata ? (
												<>
													<p>
														{quote.metadata.map((meta, index) =>
															{
															return (
																<span key={index} className='text-neutral-500'>
																	{formatName(meta)}
																	{index < quote.metadata!.length - 1 && <span> • </span>}
																</span>
															);
														})}
													</p>
												</>
											) : (
												<>
													<p>
														{!quote.url && quote.status_type && formatName(quote.status_type)}
														{quote.url && <span>{formatURL(quote.url)}</span>}
													</p>
												</>
											)}
											{quote.notes && (
												<div className='max-h-[25dvh] overflow-y-auto bg-neutral-50 p-2 rounded-md'>
													{markdownToComponent(quote.notes)}
												</div>
											)}
										</div>
									</TooltipContent>
								</Portal>
							</Tooltip>
						);
					})}
				</div>
			)}

			{evidence.references && evidence.references.length > 0 && (
				<div className='hidden //flex flex-wrap p-2 gap-2 '>
					{evidence.references.map((reference, index) =>
					{
						if (!reference)
						{
							return <></>;
						}

						const Icon = getIcon(reference.source_type);

						return (
							<div
								key={index}
								className={
									'group relative flex flex-row items-center bg-neutral-50 border gap-1 p-1 rounded-md cursor-pointer hover:bg-neutral-100 active:bg-neutral-200'
									//+ ((!sourcesExpanded && index === 1) ? ' hidden @sm:flex' : '')
									//+ ((!sourcesExpanded && index === 2) ? ' hidden @2xl:flex' : '')
								}
								onClick={e =>
								{
									//openDetail(e, reference.id, reference.source_type);
									getSource(e, reference);
								}}
							>
								<Icon className='text-icon-sm' />
								<p className={'text-start text-sm font-semibold pr-2 text-nowrap overflow-hidden text-ellipsis max-w-[40ch]'}>
									{reference.title.trim()}
								</p>
								<ExternalLink className='hidden text-icon-sm text-neutral-500' />
							</div>
						);
					})}
				</div>
			)}

			{evidence.status_tree?.data && <hr className='my-1 mb-2' />}

			{evidence.status_tree?.data && <MessageStatusTree status_tree={evidence.status_tree?.data} className='border-0' defaultOpen />}
		</div>
	);
};

export default ResearchEvidencePanel;
